import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift
} from '@floating-ui/dom';
import {
  DateRangeSelection,
  FiscalMonth
} from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';
import { CompareService } from '@portal/app/shared/services/compare.service';
import { DateTimeService } from '@portal/app/shared/services/date-time.service';
import {
  FiscalDatePresetService,
  PresetOption
} from '@portal/app/shared/services/fiscal-date-preset.service';
import { DateRange } from '@portal/app/shared/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

type InputDateType =
  | 'startDate'
  | 'endDate'
  | 'startDateCompare'
  | 'endDateCompare';

type SelectionSource = 'calendar' | 'input';

@Component({
  selector: 'portal-fiscal-calendar',
  templateUrl: './portal-fiscal-calendar.component.html',
  styleUrls: ['./portal-fiscal-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortalFiscalCalendarComponent implements OnInit, OnDestroy {
  @ViewChild('calendarInputWrapper', { static: true })
  calendarInputWrapper!: ElementRef<HTMLDivElement>;

  @ViewChild('calendarPopup', { static: true })
  calendarPopup!: ElementRef<HTMLElement>;

  readonly label = input('Time Period');
  readonly startDate = input<string>('');
  readonly endDate = input<string>('');
  readonly previousStartDate = input<string | null>(null);
  readonly previousEndDate = input<string | null>(null);
  readonly months = input<FiscalMonth[]>([]);
  readonly minDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly maxDate = input<dayjs.Dayjs | undefined>(undefined);

  readonly relativeDate = input<string | null>(null);

  @Output() readonly dateChanged = new EventEmitter<{
    startDate: string;
    endDate: string;
    previousStartDate: string | null;
    previousEndDate: string | null;
    presetValue: string;
  }>();

  showCalendar = false;
  compareToggledOn = false;
  compareEnabled = true;
  availabilityInfoText = '';

  // Preset dropdown options from service
  datePresetOptions: readonly PresetOption[] = [];
  selectedDatePreset = 'custom';
  appliedDatePreset = 'custom';

  comparePresetOptions: readonly PresetOption[] = [];
  selectedComparePreset = 'previous_period';

  private dateToShowSingleRange = '';
  private dateToShowCompareRange = '';

  readonly selected = signal<DateRange>(this.createDefaultDateRange());
  readonly selectedCompare = signal<DateRange>(this.createDefaultDateRange());

  readonly displayDates = computed(() => {
    const selectedRange = this.selected();
    const compareRange = this.selectedCompare();
    const formatDate = (date: dayjs.Dayjs) => date.format(this.displayFormat);

    return {
      start: formatDate(selectedRange.startDate),
      end: formatDate(selectedRange.endDate),
      prevStart: formatDate(compareRange.startDate),
      prevEnd: formatDate(compareRange.endDate)
    };
  });

  readonly singleRangeLabel = computed(() => {
    const dates = this.displayDates();
    return `${dates.start} - ${dates.end}`;
  });

  readonly compareRangeLabel = computed(() => {
    const dates = this.displayDates();
    return `${dates.start} - ${dates.end} | ${dates.prevStart} - ${dates.prevEnd}`;
  });

  readonly calendarDates = computed(() => {
    const selected = this.selected();
    const selectedCompare = this.selectedCompare();
    return {
      start: this.formatCalendarDate(selected.startDate),
      end: this.formatCalendarDate(selected.endDate),
      compareStart: this.formatCalendarDate(selectedCompare.startDate),
      compareEnd: this.formatCalendarDate(selectedCompare.endDate)
    };
  });

  readonly formattedMinDate = computed(() =>
    this.formatDateBound(this.minDate())
  );

  readonly formattedMaxDate = computed(() =>
    this.formatDateBound(this.maxDate())
  );

  private formatDateBound(date: dayjs.Dayjs | undefined): string | null {
    return date ? date.format(this.calendarDateFormat) : null;
  }

  private cachedSelected: DateRange = this.createDefaultDateRange();
  private cachedSelectedCompare: DateRange = this.createDefaultDateRange();
  private cachedCompareToggledOn = false;
  private cachedDatePreset = 'custom';
  private cachedComparePreset = 'previous_period';

  private cleanupPopup?: () => void;
  private isApplying = false;
  private lastSelectionSource: SelectionSource = 'input';
  private readonly displayFormat = 'MMM D, YYYY';
  private readonly editFormat = 'MM-DD-YYYY';
  private readonly calendarDateFormat = 'YYYY-MM-DD';

  private readonly dateService = inject(DateTimeService);
  private readonly compareService = inject(CompareService);
  private readonly fiscalDatePresetService = inject(FiscalDatePresetService);

  ngOnInit(): void {
    this.compareToggledOn = this.compareService.compare;
    this.compareEnabled = this.compareService.compareIsAvailable;
    this.availabilityInfoText = `Data available from ${this.formatDate(
      this.dateService.maximumEndDate(),
      'MMM D, YYYY'
    )}`;

    // Populate preset options from service
    this.datePresetOptions = this.fiscalDatePresetService.datePresetOptions;
    this.comparePresetOptions =
      this.fiscalDatePresetService.comparePresetOptions;

    // Restore saved preset from relativeDate filter (persisted across refresh)
    const savedPreset = this.relativeDate();
    if (savedPreset && savedPreset !== 'custom_range') {
      const matchingOption = this.datePresetOptions.find(
        (o) => o.value === savedPreset
      );
      if (matchingOption) {
        this.selectedDatePreset = savedPreset;
        this.appliedDatePreset = savedPreset;
      }
    }

    this.initializeDatesFromInputs();
    this.deriveComparePreset();
    this.updateAppliedLabels();
  }

  ngOnDestroy(): void {
    this.cleanupPopup?.();
  }

  private initializeDatesFromInputs(): void {
    if (this.startDate() && this.endDate()) {
      this.selected.set(this.createDateRange(this.startDate(), this.endDate()));
    }

    const prevStart = this.previousStartDate();
    const prevEnd = this.previousEndDate();
    if (prevStart && prevEnd) {
      this.selectedCompare.set(this.createDateRange(prevStart, prevEnd));
    }
  }

  toggleCalendar(event?: MouseEvent): void {
    event?.preventDefault();
    this.showCalendar ? this.closeCalendar() : this.openCalendar();
  }

  onDialogShow(): void {
    this.cleanupPopup?.();
    this.setupPopupPositioning();
  }

  onOutsideCompareToggle(value: boolean): void {
    this.cacheAppliedValues();
    this.compareToggledOn = value;
    this.openCalendar(false);
  }

  getDatePickerLabel(): string {
    if (this.appliedDatePreset !== 'custom') {
      const option = this.datePresetOptions.find(
        (o) => o.value === this.appliedDatePreset
      );
      if (option) {
        return option.label;
      }
    }

    return this.compareToggledOn
      ? this.dateToShowCompareRange
      : this.dateToShowSingleRange;
  }

  private updateAppliedLabels(): void {
    this.dateToShowSingleRange = this.singleRangeLabel();
    this.dateToShowCompareRange = this.compareRangeLabel();
  }

  onFiscalDateSelected(event: DateRangeSelection): void {
    this.lastSelectionSource = 'calendar';

    const currentSelected = this.selected();
    const primaryChanged =
      !currentSelected.startDate.isSame(
        dayjs(event.startDate).utc(true),
        'day'
      ) ||
      !currentSelected.endDate.isSame(dayjs(event.endDate).utc(true), 'day');

    if (primaryChanged) {
      this.selectedDatePreset = 'custom';
    } else {
      this.selectedComparePreset = 'custom';
    }

    this.handleCalendarSelection(event);

    if (primaryChanged && this.compareToggledOn && this.compareEnabled) {
      this.selectedComparePreset = 'previous_period';
      this.recalculateCompareRange();
    }
  }

  onInputFocus(event: FocusEvent, type: InputDateType): void {
    const input = event.target as HTMLInputElement;
    input.value = this.getDateForType(type).format(this.editFormat);
  }

  onInputBlur(event: FocusEvent, type: InputDateType): void {
    const input = event.target as HTMLInputElement;
    input.value = this.getDateForType(type).format(this.displayFormat);
  }

  private readonly dateTypeMap: Record<InputDateType, () => dayjs.Dayjs> = {
    startDate: () => this.selected().startDate,
    endDate: () => this.selected().endDate,
    startDateCompare: () => this.selectedCompare().startDate,
    endDateCompare: () => this.selectedCompare().endDate
  };

  private getDateForType(type: InputDateType): dayjs.Dayjs {
    return this.dateTypeMap[type]();
  }

  onInputChange(event: Event, type: InputDateType): void {
    this.lastSelectionSource = 'input';
    const parsed = this.parseInputDate(
      (event.target as HTMLInputElement).value
    );

    if (!parsed.isValid() || !this.isWithinBounds(parsed)) {
      return;
    }

    // Switch to Custom when user manually edits a date
    if (type === 'startDate' || type === 'endDate') {
      this.selectedDatePreset = 'custom';
    } else {
      this.selectedComparePreset = 'custom';
    }

    this.handleInputDate(parsed, type);

    if (
      (type === 'startDate' || type === 'endDate') &&
      this.compareToggledOn &&
      this.compareEnabled
    ) {
      this.selectedComparePreset = 'previous_period';
      this.recalculateCompareRange();
    }
  }

  onDatePresetChange(value: string): void {
    this.selectedDatePreset = value;
    this.lastSelectionSource = 'input';

    const range = this.fiscalDatePresetService.calculateDateRange(value);
    if (range) {
      this.selected.set({
        startDate: range.startDate,
        endDate: range.endDate
      });

      if (this.compareToggledOn && this.compareEnabled) {
        this.selectedComparePreset = 'previous_period';
        this.recalculateCompareRange();
      }
    }
  }

  onComparePresetChange(value: string): void {
    this.selectedComparePreset = value;
    this.lastSelectionSource = 'input';

    this.recalculateCompareRange();
  }

  onCompareToggle(checked: boolean): void {
    this.compareToggledOn = checked;

    if (checked) {
      this.selectedComparePreset = 'previous_period';
      this.recalculateCompareRange();
    }
  }

  private deriveComparePreset(): void {
    if (!this.compareToggledOn || !this.compareEnabled) {
      return;
    }

    const currentRange = this.selected();
    const savedCompare = this.selectedCompare();

    for (const option of this.comparePresetOptions) {
      if (option.value === 'custom') {
        continue;
      }

      const calculated = this.fiscalDatePresetService.calculateCompareDateRange(
        option.value,
        {
          startDate: currentRange.startDate,
          endDate: currentRange.endDate
        }
      );

      if (
        calculated &&
        calculated.startDate.isSame(savedCompare.startDate, 'day') &&
        calculated.endDate.isSame(savedCompare.endDate, 'day')
      ) {
        this.selectedComparePreset = option.value;
        return;
      }
    }

    this.selectedComparePreset = 'custom';
  }

  private recalculateCompareRange(): void {
    const currentRange = this.selected();
    const compareRange = this.fiscalDatePresetService.calculateCompareDateRange(
      this.selectedComparePreset,
      { startDate: currentRange.startDate, endDate: currentRange.endDate }
    );

    if (compareRange) {
      this.selectedCompare.set({
        startDate: compareRange.startDate,
        endDate: compareRange.endDate
      });
    }
  }

  apply(): void {
    this.isApplying = true;

    if (this.lastSelectionSource === 'input') {
      this.syncDatesFromInputFields();
    }

    this.appliedDatePreset = this.selectedDatePreset;
    this.updateAppliedLabels();
    this.emitDateRangeData();
    this.closeCalendar();
  }

  cancel(): void {
    if (this.isApplying) {
      this.isApplying = false;
      return;
    }
    this.restoreCachedValues();
    this.closeCalendar();
  }

  private handleCalendarSelection(event: DateRangeSelection): void {
    this.selected.set(this.createDateRange(event.startDate, event.endDate));

    if (event.previousStartDate && event.previousEndDate) {
      this.selectedCompare.set(
        this.createDateRange(event.previousStartDate, event.previousEndDate)
      );
    }
  }

  private handleInputDate(date: dayjs.Dayjs, type: InputDateType): void {
    switch (type) {
      case 'startDate':
        this.selected.update((current) => ({
          startDate: date,
          endDate: date.isAfter(current.endDate) ? date : current.endDate
        }));
        break;

      case 'endDate':
        this.selected.update((current) => ({
          startDate: current.startDate,
          endDate: date.isBefore(current.startDate) ? current.startDate : date
        }));
        break;

      case 'startDateCompare':
        this.selectedCompare.update((current) =>
          date.isAfter(current.endDate)
            ? current
            : { startDate: date, endDate: current.endDate }
        );
        break;

      case 'endDateCompare':
        this.selectedCompare.update((current) =>
          date.isBefore(current.startDate)
            ? current
            : { startDate: current.startDate, endDate: date }
        );
        break;
    }
  }

  private syncDatesFromInputFields(): void {
    const dates = this.displayDates();

    const currentStart = this.parseInputDate(dates.start);
    const currentEnd = this.parseInputDate(dates.end);

    if (currentStart.isValid() && currentEnd.isValid()) {
      this.selected.set(this.createDateRange(currentStart, currentEnd));
    }

    if (this.compareToggledOn) {
      const compareStart = this.parseInputDate(dates.prevStart);
      const compareEnd = this.parseInputDate(dates.prevEnd);

      if (compareStart.isValid() && compareEnd.isValid()) {
        this.selectedCompare.set(
          this.createDateRange(compareStart, compareEnd)
        );
      }
    }
  }

  private snapshotState(source?: {
    current?: DateRange;
    compare?: DateRange;
    toggled?: boolean;
  }): void {
    this.cachedSelected = { ...(source?.current ?? this.selected()) };
    this.cachedSelectedCompare = {
      ...(source?.compare ?? this.selectedCompare())
    };
    this.cachedCompareToggledOn = source?.toggled ?? this.compareToggledOn;
    this.cachedDatePreset = this.selectedDatePreset;
    this.cachedComparePreset = this.selectedComparePreset;
  }

  private cacheCurrentValues(): void {
    this.snapshotState();
  }

  private cacheAppliedValues(): void {
    this.snapshotState({
      current: this.compareService.currentDate ?? undefined,
      compare: this.compareService.previousDate ?? undefined,
      toggled: this.compareService.compare
    });
  }

  private restoreCachedValues(): void {
    this.selected.set({ ...this.cachedSelected });
    this.selectedCompare.set({ ...this.cachedSelectedCompare });
    this.compareToggledOn = this.cachedCompareToggledOn;
    this.selectedDatePreset = this.cachedDatePreset;
    this.selectedComparePreset = this.cachedComparePreset;
  }

  private emitDateRangeData(): void {
    const dates = this.calendarDates();

    this.compareService.currentDate = this.selected();
    this.compareService.previousDate = this.selectedCompare();
    this.compareService.compare = this.compareToggledOn;
    this.compareService.setCompareValue(this.compareToggledOn);

    this.cacheCurrentValues();

    this.dateChanged.emit({
      startDate: dates.start,
      endDate: dates.end,
      previousStartDate: this.compareToggledOn ? dates.compareStart : null,
      previousEndDate: this.compareToggledOn ? dates.compareEnd : null,
      presetValue: this.appliedDatePreset
    });
  }

  private setupPopupPositioning(): void {
    const referenceEl = this.calendarInputWrapper.nativeElement;
    const popupEl = this.calendarPopup.nativeElement;

    this.cleanupPopup = autoUpdate(referenceEl, popupEl, () => {
      computePosition(referenceEl, popupEl, {
        placement: 'bottom-start',
        middleware: [offset(7), flip(), shift({ padding: 7 })]
      }).then(({ x, y }) => {
        Object.assign(popupEl.style, {
          left: `${x}px`,
          top: `${y}px`
        });
      });
    });
  }

  private openCalendar(cacheValues = true): void {
    if (this.showCalendar) return;
    this.showCalendar = true;
    if (cacheValues) this.cacheCurrentValues();
  }

  private closeCalendar(): void {
    this.cleanupPopup?.();
    this.showCalendar = false;
  }

  private isWithinBounds(date: dayjs.Dayjs): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min && date.isBefore(min, 'day')) return false;
    if (max && date.isAfter(max, 'day')) return false;
    return true;
  }

  private createDefaultDateRange(): DateRange {
    const now = dayjs().utc(true);
    return { startDate: now, endDate: now };
  }

  private createDateRange(
    start: string | Date | dayjs.Dayjs,
    end: string | Date | dayjs.Dayjs
  ): DateRange {
    return {
      startDate: this.toUtcDayjs(start),
      endDate: this.toUtcDayjs(end)
    };
  }

  private toUtcDayjs(value: string | Date | dayjs.Dayjs): dayjs.Dayjs {
    return dayjs.isDayjs(value) ? value.utc(true) : dayjs(value).utc(true);
  }

  private parseInputDate(value: string): dayjs.Dayjs {
    // Try edit format first (MM-DD-YYYY), then display format (MMM D, YYYY)
    const editParsed = dayjs(value, this.editFormat, true).utc(true);
    if (editParsed.isValid()) {
      return editParsed;
    }
    return dayjs(value, this.displayFormat).utc(true);
  }

  private formatCalendarDate(date: dayjs.Dayjs): string {
    return date.format(this.calendarDateFormat);
  }

  formatDate(date: dayjs.Dayjs, template = this.displayFormat): string {
    return date.format(template);
  }
}
